import React, { useEffect, useRef, useState } from "react";

// Scroll-reveal primitive. Everything that animates in on this site goes
// through it, so there is exactly one place that decides the curve, the
// distance and the trigger point.
//
// Deliberately restrained: a short rise plus fade, once, never replayed. The
// content is dense technical prose and heavy motion fights it.
//
// Reduced motion is honoured twice over: the CSS layer neutralises durations
// globally, and this component skips the hidden state entirely so nothing can
// get stuck invisible if an observer never fires.

const REDUCED =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function useReveal(options) {
  const ref = useRef(null);
  const [shown, setShown] = useState(REDUCED);

  useEffect(() => {
    if (shown) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.04, ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, options]);

  return [ref, shown];
}

export default function Reveal({
  as: Tag = "div",
  delay = 0,
  distance = 14,
  className = "",
  style,
  children,
  ...rest
}) {
  const [ref, shown] = useReveal();

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : `translateY(${distance}px)`,
        transition:
          "opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: shown ? `${delay}ms` : "0ms",
        willChange: shown ? "auto" : "opacity, transform",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// Staggered list. Children reveal one after another rather than as a slab,
// which reads as the page assembling itself instead of blinking on.
export function RevealList({
  as: Tag = "div",
  itemAs = "div",
  step = 55,
  max = 8,
  delay = 0,
  className = "",
  itemClassName = "",
  items,
  children: render,
  ...rest
}) {
  return (
    <Tag className={className} {...rest}>
      {items.map((item, i) => (
        <Reveal
          key={item.key ?? i}
          as={itemAs}
          delay={delay + Math.min(i, max) * step}
          distance={10}
          className={itemClassName}
        >
          {render(item, i)}
        </Reveal>
      ))}
    </Tag>
  );
}
